/**
 * Timeout + fetch + retries for 5xx / network errors + request/response telemetry hooks.
 * Auth headers and status handling stay in {@link HttpClient}.
 */
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import {
  computeBackoffDelayMs,
  sleep,
  type RetryOptions,
} from '@core/domain/utils/asyncResilience.ts';
import { isRetryableHttpResponseStatus } from '@core/domain/utils/httpTransportFailure.ts';
import {
  executeHttpFetch,
  headersInitToRecord,
  type HttpTransportRequestOptions,
  type HttpTransportResponse,
} from './httpFetchTransport.ts';

export type { HttpTransportRequestOptions, HttpTransportResponse } from './httpFetchTransport.ts';

export type HttpFetchRequestOptions = RequestInit & {
  timeout?: number;
  retries?: number;
  treat401AsPlainError?: boolean;
};

export type HttpFetchRetryDeps = {
  telemetry: TelemetryPort;
  defaultTimeout: number;
  retryBackoff: RetryOptions;
  buildHeaders: (extra: HeadersInit | undefined, correlationId: string) => HeadersInit;
};

function transportMethod(method: string): HttpTransportRequestOptions['method'] {
  const m = method.toUpperCase();
  if (m === 'POST' || m === 'PUT' || m === 'DELETE' || m === 'HEAD') return m;
  return 'GET';
}

function emitHttpSuccess(
  telemetry: TelemetryPort,
  correlationId: string,
  method: string,
  url: string,
  status: number,
  durationMs: number,
): void {
  telemetry.logHttpResponse({ correlationId, status, durationMs, url });
}

/** Executes an HTTP fetch request with timeout, retries on 5xx/network errors, and telemetry hooks. */
export async function fetchWithTimeoutAndRetry(
  url: string,
  options: HttpFetchRequestOptions,
  retriesLeft: number,
  deps: HttpFetchRetryDeps,
  correlationId?: string,
  totalRetries?: number,
): Promise<{ response: HttpTransportResponse; correlationId: string }> {
  const total = totalRetries ?? retriesLeft;
  const cid = correlationId ?? createCorrelationId();
  const {
    timeout = deps.defaultTimeout,
    retries: _r,
    treat401AsPlainError: _401,
    ...fetchOptions
  } = options;
  const method = (fetchOptions.method as string) || 'GET';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  deps.telemetry.logHttpRequest({ correlationId: cid, method, url });
  const t0 = performance.now();

  try {
    const response = await executeHttpFetch(url, {
      method: transportMethod(method),
      headers: headersInitToRecord(deps.buildHeaders(fetchOptions.headers, cid)),
      body: fetchOptions.body as HttpTransportRequestOptions['body'],
      timeoutMs: timeout,
      signal: controller.signal,
    });
    clearTimeout(timer);
    try {
      emitHttpSuccess(deps.telemetry, cid, method, url, response.status, performance.now() - t0);
    } catch {
      /* HTTP response remains valid even if telemetry fails */
    }

    if (!response.ok && isRetryableHttpResponseStatus(response.status) && retriesLeft > 0) {
      deps.telemetry.logError(`HTTP ${response.status} on ${url}, retrying...`, {
        correlationId: cid,
        status: response.status,
        url,
      });
      const attemptIndex = total - retriesLeft;
      await sleep(computeBackoffDelayMs(attemptIndex, deps.retryBackoff));
      return fetchWithTimeoutAndRetry(url, options, retriesLeft - 1, deps, cid, total);
    }
    return { response, correlationId: cid };
  } catch (error) {
    clearTimeout(timer);
    const err = error as Error;
    if (err.name === 'AbortError' || err.message === 'TimeoutError') {
      deps.telemetry.logError(`Request timeout for ${url}`, {
        correlationId: cid,
        url,
        timeout,
        durationMs: performance.now() - t0,
      });
      throw new Error('Request timeout');
    }
    if (retriesLeft > 0) {
      deps.telemetry.logError(`Network error on ${url}, retrying...`, { error, url, correlationId: cid });
      const attemptIndex = total - retriesLeft;
      await sleep(computeBackoffDelayMs(attemptIndex, deps.retryBackoff));
      return fetchWithTimeoutAndRetry(url, options, retriesLeft - 1, deps, cid, total);
    }
    deps.telemetry.logError(`Failed to fetch ${url}`, { error, url, correlationId: cid });
    throw error;
  }
}
